import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Grid,
  Divider
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Event as EventIcon,
  Cancel as CancelIcon,
  Update as RescheduleIcon,
  Person as DoctorIcon,
  Schedule as TimeIcon,
  ArrowBack as BackIcon
} from "@material-ui/icons";
import { format, parseISO, isPast } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "react-toastify";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";

registerLocale("es", es);

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    maxWidth: 800,
    margin: "0 auto",
  },
  card: {
    marginBottom: theme.spacing(2),
    border: "1px solid " + theme.palette.divider,
    borderRadius: 8,
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    }
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  timeChip: {
    margin: theme.spacing(0.5),
    fontWeight: 600,
    padding: theme.spacing(1),
    "&.selected": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
    "&.disabled": {
      opacity: 0.5,
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
      pointerEvents: "none"
    }
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(5),
    color: theme.palette.text.secondary
  },
  confirmationBox: {
    backgroundColor: "rgba(33, 150, 243, 0.08)",
    padding: theme.spacing(2),
    borderRadius: 8,
    border: "1px solid rgba(33, 150, 243, 0.3)",
    marginBottom: theme.spacing(2)
  }
}));

const RescheduleView = ({ data }) => {
  const classes = useStyles();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [rescheduleStep, setRescheduleStep] = useState(0);
  const [doctorSlots, setDoctorSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (data.appointments) {
      const validAppointments = data.appointments.filter(appt => {
        const apptDate = parseISO(appt.date + "T" + appt.startTime);
        return appt.status === "scheduled" && !isPast(apptDate);
      });
      setAppointments(validAppointments);
    }
  }, [data]);

  const handleOpenCancel = (appt) => {
    setSelectedAppointment(appt);
    setCancelReason("");
    setCancelModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) return;
    setLoading(true);
    try {
      await api.patch("/public/appointments/" + selectedAppointment.id, {
        token: data.token,
        status: "cancelled",
        notes: cancelReason
      });
      toast.success("Cita cancelada correctamente");
      setCancelModalOpen(false);
      setAppointments(prev => prev.filter(a => a.id !== selectedAppointment.id));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReschedule = (appt) => {
    setSelectedAppointment(appt);
    setRescheduleStep(0);
    setSelectedDate(new Date());
    setSelectedTimeSlot(null);
    setRescheduleModalOpen(true);
  };

  useEffect(() => {
    if (rescheduleModalOpen && selectedAppointment) {
      const fetchAvailability = async () => {
        setLoadingSlots(true);
        try {
          const { data: res } = await api.get("/public/availability", {
            params: { doctorId: selectedAppointment.doctor.id }
          });
          setDoctorSlots(res.slots || []);
        } catch (err) {
          console.error(err);
          toastError("Error al cargar horarios");
        } finally {
          setLoadingSlots(false);
        }
      };
      fetchAvailability();
    }
  }, [rescheduleModalOpen, selectedAppointment]);

  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate || doctorSlots.length === 0) return [];
    
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const now = new Date();

    const daySlots = doctorSlots.filter(s => s.date === dateStr);
    return daySlots.map(s => {
        const slotDateTime = new Date(s.date + "T" + s.startTime);
        const isPastSlot = slotDateTime < now;
        return {
            ...s,
            endTime: s.endTime, 
            label: s.startTime.slice(0, 5),
            disabled: isPastSlot || s.status !== "free"
        };
    }).sort((a, b) => a.startTime.localeCompare(b.startTime));

  }, [selectedDate, doctorSlots]);

  const handleConfirmReschedule = async () => {
    if (!selectedTimeSlot) return;
    setLoading(true);
    try {
      await api.patch("/public/appointments/" + selectedAppointment.id, {
        token: data.token,
        date: selectedTimeSlot.date,
        startTime: selectedTimeSlot.startTime,
        endTime: selectedTimeSlot.endTime
      });
      
      toast.success("Cita reagendada con éxito");
      setRescheduleModalOpen(false);

      const { data: updated } = await api.get("/public/appointments", {
        params: { token: data.token }
      });
      setAppointments(updated.appointments);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Box mb={3} textAlign="center">
        <Typography variant="h5" style={{ fontWeight: 700 }}>
          Gestionar Citas Pendientes
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Hola {data.patientName}, aquí puedes modificar tus próximas visitas.
        </Typography>
      </Box>

      {appointments.length === 0 ? (
        <div className={classes.emptyState}>
             <EventIcon style={{ fontSize: 60, opacity: 0.2, marginBottom: 16 }} />
             <Typography variant="h6">No tienes citas pendientes</Typography>
             <Typography variant="body2">
                Si necesitas atención, por favor agenda una nueva cita.
             </Typography>
        </div>
      ) : (
        <List>
          {appointments.map((appt) => {
            const dateObj = parseISO(appt.date + "T" + appt.startTime);
            const dateStr = format(dateObj, "EEEE d 'de' MMMM 'a las' HH:mm", { locale: es });
            
            return (
              <Paper key={appt.id} className={classes.card} variant="outlined">
                <ListItem>
                  <ListItemText
                    primary={
                        <Box display="flex" alignItems="center">
                            <EventIcon color="primary" style={{ marginRight: 8 }} />
                            <Typography variant="h6" style={{ fontSize: "1rem", fontWeight: 600 }}>
                                {dateStr} hs
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box mt={1} display="flex" alignItems="center">
                            <DoctorIcon fontSize="small" style={{ marginRight: 4, opacity: 0.7 }} />
                            <Typography variant="body2" component="span">
                                {appt.doctor?.name} ({appt.professionalTitle?.title})
                            </Typography>
                        </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                        <Button
                            size="small"
                            color="secondary"
                            onClick={() => handleOpenCancel(appt)}
                            startIcon={<CancelIcon />}
                            style={{ marginBottom: 8 }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => handleOpenReschedule(appt)}
                            startIcon={<RescheduleIcon />}
                        >
                            Reagendar
                        </Button>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            );
          })}
        </List>
      )}

      <Dialog open={cancelModalOpen} onClose={() => setCancelModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar Cita</DialogTitle>
        <DialogContent dividers>
            <Typography gutterBottom>
                ¿Estás seguro de que deseas cancelar tu cita con <strong>{selectedAppointment?.doctor?.name}</strong>?
            </Typography>
            <TextField
                autoFocus
                margin="dense"
                label="Motivo de cancelación (Requerido)"
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setCancelModalOpen(false)} color="default" disabled={loading}>
                Volver
            </Button>
            <Button onClick={handleConfirmCancel} color="secondary" variant="contained" disabled={loading || !cancelReason.trim()}>
                {loading ? <CircularProgress size={24} /> : "Confirmar Cancelación"}
            </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rescheduleModalOpen} onClose={() => setRescheduleModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
             {rescheduleStep === 0 ? "Selecciona Nueva Fecha" : "Confirmar Cambio"}
        </DialogTitle>
        
        <DialogContent dividers>
            {rescheduleStep === 0 && (
                <Grid container spacing={4}>
                    <Grid item xs={12} md={5} style={{ display: "flex", justifyContent: "center" }}>
                        <DatePicker
                            selected={selectedDate}
                            onChange={(date) => { setSelectedDate(date); setSelectedTimeSlot(null); }}
                            inline
                            minDate={new Date()}
                            locale="es"
                        />
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Typography variant="subtitle2" gutterBottom>Horarios Disponibles:</Typography>
                        {loadingSlots ? (
                            <Box display="flex" justifyContent="center" p={2}><CircularProgress /></Box>
                        ) : slotsForSelectedDate.length > 0 ? (
                            <Box display="flex" flexWrap="wrap">
                                {slotsForSelectedDate.map((slot, index) => (
                                    <Chip
                                        key={index}
                                        label={slot.label}
                                        onClick={() => !slot.disabled && setSelectedTimeSlot(slot)}
                                        clickable={!slot.disabled}
                                        color={selectedTimeSlot?.label === slot.label ? "primary" : "default"}
                                        className={classes.timeChip + (selectedTimeSlot?.label === slot.label ? " selected" : "") + (slot.disabled ? " disabled" : "")}
                                        variant={selectedTimeSlot?.label === slot.label ? "default" : "outlined"}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography color="error">No hay horarios disponibles para este día.</Typography>
                        )}
                    </Grid>
                </Grid>
            )}

            {rescheduleStep === 1 && selectedTimeSlot && selectedAppointment && (
                <Box>
                    <div className={classes.confirmationBox}>
                        <Typography variant="body1" gutterBottom>
                            <strong>Cambio solicitado:</strong>
                        </Typography>
                        <Box display="flex" alignItems="center" my={1} color="text.secondary" style={{ textDecoration: "line-through" }}>
                            <EventIcon fontSize="small" style={{ marginRight: 8 }} />
                            {format(parseISO(selectedAppointment.date + "T" + selectedAppointment.startTime), "dd/MM/yyyy HH:mm")} hs
                        </Box>
                        <Box display="flex" alignItems="center" my={1} color="primary.main" style={{ fontWeight: "bold" }}>
                            <RescheduleIcon fontSize="small" style={{ marginRight: 8 }} />
                            {format(parseISO(selectedTimeSlot.date + "T" + selectedTimeSlot.startTime), "dd/MM/yyyy HH:mm")} hs
                        </Box>
                    </div>
                    <Typography variant="body2" color="textSecondary">
                        Al confirmar, se liberará tu horario anterior y se reservará el nuevo espacio.
                    </Typography>
                </Box>
            )}
        </DialogContent>

        <DialogActions>
            {rescheduleStep === 0 ? (
                <>
                    <Button onClick={() => setRescheduleModalOpen(false)} color="default">
                        Cancelar
                    </Button>
                    <Button 
                        onClick={() => setRescheduleStep(1)} 
                        color="primary" 
                        variant="contained"
                        disabled={!selectedTimeSlot}
                    >
                        Siguiente
                    </Button>
                </>
            ) : (
                <>
                    <Button onClick={() => setRescheduleStep(0)} startIcon={<BackIcon />}>
                        Atrás
                    </Button>
                    <Button 
                        onClick={handleConfirmReschedule} 
                        color="primary" 
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Confirmar Cambio"}
                    </Button>
                </>
            )}
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default RescheduleView;